import { useCallback, useEffect, useMemo } from 'react';
import {
  Flex,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import type { IConfigureStepProps, TConfigurationValues } from './types';
import {
  AddConnectionDialog,
  useConnectionActions,
} from '@/modules/integrations';
import {
  useRememberTriggerForResume,
  useTriggerOAuthConnection,
} from '@/modules/triggers/hooks/use-trigger-oauth-connection';
import { extractTriggerFieldGroups } from './utils';
import { extractFilterableFields, type IFilterCondition } from './filter-utils';
import { WebhookConfiguration } from './WebhookConfiguration';
import { TriggerFilters } from './TriggerFilters';
import { ConfigFormRow } from './ConfigFormRow';
import { DEFAULT_FILTER_CONDITIONS } from './constants';
import { EXCLUDED_APP_NAMES } from '../EmailTriggerDialog/constants';
import { useTriggerConfigureData } from '../shared/useTriggerConfigureData';
import { IntegrationSelectRow } from '../shared/IntegrationSelectRow';
import { ConnectionSelectRow } from '../shared/ConnectionSelectRow';
import type { V2BookTriggerDescriptor } from '@/generated/hooks/bdk/models';

/**
 * ConfigureStep Component
 *
 * The first step of the webhook trigger dialog where users select
 * the integration, connection, and event for the webhook.
 *
 * @param props - Component props
 * @returns JSX element containing the configuration form
 */
export function ConfigureStep({
  selectedIntegration,
  onIntegrationChange,
  onIntegrationVersionChange,
  selectedConnection,
  onConnectionChange,
  selectedEvent,
  onEventChange,
  onTriggerFieldGroupsExtracted,
  configurationValues,
  onConfigurationChange,
  readOnly = false,
  hasNextStep = false,
  lockSelections = readOnly,
  configErrors,
  onSelectedTriggerChange,
  onSelectedIntegrationDisplayNameChange,
  filterConditions = DEFAULT_FILTER_CONDITIONS,
  onFilterConditionsChange,
  filterErrors,
  highlightedConfigKey,
  highlightConfigNonce,
  highlightedFilterId,
  highlightFilterNonce,
  triggerKind = 'webhook',
}: IConfigureStepProps) {
  const { t } = useTranslation('automation-inputs');

  // Shared integration + connection data logic
  const {
    organizationId,
    workspaceId,
    uniqueIntegrations,
    isLoadingIntegrations,
    isLoadingConnections,
    draftConnections,
    selectedIntegrationData,
    handleConnectionChange,
    isAddConnectionDialogOpen,
    openAddConnectionDialog,
    closeAddConnectionDialog,
    handleConnectionSuccess,
    latestIntegrationVersion,
  } = useTriggerConfigureData({
    selectedIntegration,
    selectedConnection,
    onConnectionChange,
    onIntegrationVersionChange,
    booksFilterField: 'has_triggers',
    excludedAppNames: EXCLUDED_APP_NAMES,
  });

  // Connection actions for AddConnectionDialog
  const {
    createConnection,
    updateConnection,
    createOAuthAuthorizationConnection,
  } = useConnectionActions(
    organizationId,
    workspaceId,
    selectedIntegration,
    latestIntegrationVersion,
  );

  const rememberForResume = useRememberTriggerForResume({
    triggerKind,
    integration: selectedIntegrationData,
  });
  const handleOAuthClick = useTriggerOAuthConnection({
    triggerKind,
    integration: selectedIntegrationData,
    createOAuthAuthorizationConnection,
  });

  // Get triggers from the selected integration
  const triggers = useMemo<V2BookTriggerDescriptor[]>(
    () => selectedIntegrationData?.triggers ?? [],
    [selectedIntegrationData?.triggers],
  );

  // Find the currently selected trigger
  const selectedTrigger = useMemo(
    () => triggers.find(t => t.id === selectedEvent),
    [triggers, selectedEvent],
  );

  // Top-level event fields that can be filtered on (v1: scalars/enums only).
  const filterableFields = useMemo(
    () => extractFilterableFields(selectedTrigger),
    [selectedTrigger],
  );

  // Offer filtering whenever the selected event exposes filterable top-level
  // fields. We intentionally don't gate on the trigger's `filter_capable` flag
  // since it isn't reliably populated.
  const showFilters = filterableFields.length > 0;

  // Default empty configuration values
  const defaultConfigValues: TConfigurationValues = {};

  // Extract trigger field groups when component mounts with pre-selected event (edit mode)
  // or when triggers data becomes available for an already selected event
  // eslint-disable-next-line no-restricted-syntax -- reason: Need to extract trigger fields when edit mode opens with pre-selected event
  useEffect(() => {
    if (selectedEvent && triggers.length > 0) {
      const selectedTrigger = triggers.find(t => t.id === selectedEvent);
      if (selectedTrigger) {
        const triggerFieldGroups = extractTriggerFieldGroups(selectedTrigger);
        onTriggerFieldGroupsExtracted?.(triggerFieldGroups);
      }
    }
  }, [selectedEvent, triggers, onTriggerFieldGroupsExtracted]);

  // Bubble the resolved trigger descriptor up so the dialog can validate
  // configuration values against its schema on Save.
  // eslint-disable-next-line no-restricted-syntax -- reason: notify parent when resolved trigger changes
  useEffect(() => {
    onSelectedTriggerChange?.(selectedTrigger);
  }, [selectedTrigger, onSelectedTriggerChange]);

  // Bubble the integration's display name up so the dialog can title the
  // assistant thread with it rather than the raw book id.
  // eslint-disable-next-line no-restricted-syntax -- reason: notify parent when resolved integration changes
  useEffect(() => {
    onSelectedIntegrationDisplayNameChange?.(
      selectedIntegrationData?.display_name,
    );
  }, [selectedIntegrationData, onSelectedIntegrationDisplayNameChange]);

  /**
   * Handles event selection change.
   * Extracts trigger field groups from the selected trigger's event_type
   * and passes them to the parent component.
   */
  const handleEventChange = useCallback(
    (eventId: string) => {
      onEventChange?.(eventId);

      // Find the selected trigger and extract field groups
      const selectedTrigger = triggers.find(t => t.id === eventId);
      const triggerFieldGroups = extractTriggerFieldGroups(selectedTrigger);
      onTriggerFieldGroupsExtracted?.(triggerFieldGroups);

      // Filters are scoped to a specific event's fields, so clear them when the
      // event changes — stale rows would reference fields the new event may not have.
      onFilterConditionsChange?.(DEFAULT_FILTER_CONDITIONS);
    },
    [
      triggers,
      onEventChange,
      onTriggerFieldGroupsExtracted,
      onFilterConditionsChange,
    ],
  );

  /**
   * Handles integration selection change. Clears filters since they only make
   * sense for the previously selected integration's event.
   */
  const handleIntegrationChange = useCallback(
    (value: string) => {
      onIntegrationChange?.(value);
      onFilterConditionsChange?.(DEFAULT_FILTER_CONDITIONS);
    },
    [onIntegrationChange, onFilterConditionsChange],
  );

  const handleFilterConditionsChange = useCallback(
    (conditions: IFilterCondition[]) => onFilterConditionsChange?.(conditions),
    [onFilterConditionsChange],
  );

  return (
    <Flex direction="column" gap={2} className="rounded-lg w-full">
      <div className="bg-secondary rounded-lg p-5 w-full">
        {/* Select Integration */}
        <IntegrationSelectRow
          label={t('webhookDialog.configure.selectIntegration.label')}
          description={t(
            'webhookDialog.configure.selectIntegration.description',
          )}
          placeholder={t(
            'webhookDialog.configure.selectIntegration.placeholder',
          )}
          selectedIntegration={selectedIntegration}
          onIntegrationChange={handleIntegrationChange}
          uniqueIntegrations={uniqueIntegrations}
          isLoading={isLoadingIntegrations}
          disabled={lockSelections}
        />

        {/* Select Connection */}
        <ConnectionSelectRow
          label={t('webhookDialog.configure.selectConnection.label')}
          description={t(
            'webhookDialog.configure.selectConnection.description',
          )}
          placeholder={t(
            'webhookDialog.configure.selectConnection.placeholder',
          )}
          newConnectionLabel={t(
            'webhookDialog.configure.selectConnection.newConnection',
          )}
          inactiveLabel={t(
            hasNextStep
              ? 'trigger.connection.inactiveContinue'
              : 'trigger.connection.inactiveSave',
          )}
          selectedIntegration={selectedIntegration}
          selectedConnection={selectedConnection}
          onConnectionChange={handleConnectionChange}
          draftConnections={draftConnections}
          isLoading={isLoadingConnections || isLoadingIntegrations}
          lockSelections={lockSelections}
          onAddConnection={openAddConnectionDialog}
        />

        {/* Select Event */}
        <ConfigFormRow
          label={t('webhookDialog.configure.selectEvent.label')}
          description={t('webhookDialog.configure.selectEvent.description')}
          disabled={!selectedConnection}
        >
          {isLoadingIntegrations ? (
            <Skeleton className="h-9 w-full rounded-md bg-alternate-1" />
          ) : (
            <Select
              value={selectedEvent}
              onValueChange={handleEventChange}
              disabled={!selectedConnection || lockSelections}
            >
              <SelectTrigger className="w-full bg-background">
                <SelectValue
                  placeholder={t(
                    'webhookDialog.configure.selectEvent.placeholder',
                  )}
                />
              </SelectTrigger>
              <SelectContent>
                {triggers.map(trigger => (
                  <SelectItem key={trigger.id} value={trigger.id ?? ''}>
                    {trigger.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </ConfigFormRow>
      </div>

      {/* Webhook Configuration - Render when trigger has configuration */}
      {selectedTrigger?.configuration &&
        Object.keys(selectedTrigger.configuration).length > 0 && (
          <WebhookConfiguration
            configuration={selectedTrigger.configuration}
            values={configurationValues ?? defaultConfigValues}
            onValueChange={(key, value) => onConfigurationChange?.(key, value)}
            readOnly={readOnly}
            errors={configErrors}
            highlightedKey={highlightedConfigKey}
            highlightNonce={highlightConfigNonce}
          />
        )}

      {/* Filters - Render when the trigger has filterable fields */}
      {showFilters && (
        <TriggerFilters
          key={selectedEvent}
          fields={filterableFields}
          conditions={filterConditions}
          onConditionsChange={handleFilterConditionsChange}
          errors={filterErrors}
          readOnly={readOnly}
          highlightedConditionId={highlightedFilterId}
          highlightNonce={highlightFilterNonce}
        />
      )}

      {/* Add Connection Dialog */}
      {selectedIntegrationData && (
        <AddConnectionDialog
          isOpen={isAddConnectionDialogOpen}
          onCancel={closeAddConnectionDialog}
          onSuccess={handleConnectionSuccess}
          onOAuthClick={handleOAuthClick}
          onManagedSignInRedirect={rememberForResume}
          integration={selectedIntegrationData}
          createConnection={createConnection}
          updateConnection={updateConnection}
        />
      )}
    </Flex>
  );
}
